
import React from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { wilayas, communes } from '@/data/algeria';
import { User, Phone, MapPin, Map, Mail, Home } from 'lucide-react';

interface SimpleOrderFormProps {
  formData: {
    quantity: number;
    customerName: string;
    customerPhone: string;
    customerEmail: string;
    customerAddress: string;
    customerRegion: string;
    customerMunicipality: string;
    note: string;
  };
  setters: {
    setQuantity: (value: number) => void;
    setCustomerName: (value: string) => void;
    setCustomerPhone: (value: string) => void;
    setCustomerEmail: (value: string) => void;
    setCustomerAddress: (value: string) => void;
    setCustomerRegion: (value: string) => void;
    setCustomerMunicipality: (value: string) => void;
    setNote: (value: string) => void;
  };
  formId?: string;
}

const SimpleOrderForm: React.FC<SimpleOrderFormProps> = ({ formData, setters, formId }) => {
  const {
    customerName,
    customerPhone,
    customerEmail,
    customerAddress,
    customerRegion,
    customerMunicipality,
    note
  } = formData;

  const {
    setCustomerName,
    setCustomerPhone,
    setCustomerEmail,
    setCustomerAddress,
    setCustomerRegion,
    setCustomerMunicipality,
    setNote
  } = setters;

  // Filtrer les communes en fonction de la wilaya sélectionnée
  const filteredCommunes = customerRegion
    ? communes.filter(commune => commune.wilaya_code === customerRegion)
    : [];

  return (
    <div className="space-y-4">
      {/* Suppression du champ quantité en haut du formulaire */}
      
      {/* Nom complet */}
      <div className="space-y-2">
        <Label htmlFor="customerName">Nom Complet <span className="text-red-500">*</span></Label>
        <div className="flex">
          <div className="bg-gray-200 flex items-center justify-center p-2 rounded-l-md">
            <User className="h-4 w-4 text-gray-500" />
          </div>
          <Input
            id="customerName"
            placeholder="الاسم"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            required
            className="rounded-l-none"
          />
        </div>
      </div>

      {/* Numéro de téléphone */}
      <div className="space-y-2">
        <Label htmlFor="customerPhone">Numéro De Téléphone <span className="text-red-500">*</span></Label>
        <div className="flex">
          <div className="bg-gray-200 flex items-center justify-center p-2 rounded-l-md">
            <Phone className="h-4 w-4 text-gray-500" />
          </div>
          <Input
            id="customerPhone"
            placeholder="الهاتف"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            required
            className="rounded-l-none"
          />
        </div>
      </div>

      {/* Email */}
      <div className="space-y-2">
        <Label htmlFor="customerEmail">Email</Label>
        <div className="flex">
          <div className="bg-gray-200 flex items-center justify-center p-2 rounded-l-md">
            <Mail className="h-4 w-4 text-gray-500" />
          </div>
          <Input
            id="customerEmail"
            type="email"
            placeholder="البريد الإلكتروني"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            className="rounded-l-none"
          />
        </div>
      </div>

      {/* Wilaya */}
      <div className="space-y-2">
        <Label htmlFor="customerRegion">Wilaya <span className="text-red-500">*</span></Label>
        <div className="flex">
          <div className="bg-gray-200 flex items-center justify-center p-2 rounded-l-md">
            <Map className="h-4 w-4 text-gray-500" />
          </div>
          <Select value={customerRegion} onValueChange={setCustomerRegion} required>
            <SelectTrigger id="customerRegion" className="rounded-l-none w-full">
              <SelectValue placeholder="الولاية" />
            </SelectTrigger>
            <SelectContent>
              {wilayas.map((wilaya) => (
                <SelectItem key={wilaya.code} value={wilaya.code}>
                  {wilaya.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Commune */}
      <div className="space-y-2">
        <Label htmlFor="customerMunicipality">Commune <span className="text-red-500">*</span></Label>
        <div className="flex">
          <div className="bg-gray-200 flex items-center justify-center p-2 rounded-l-md">
            <MapPin className="h-4 w-4 text-gray-500" />
          </div>
          <Select 
            value={customerMunicipality} 
            onValueChange={setCustomerMunicipality}
            disabled={!customerRegion}
            required
          >
            <SelectTrigger id="customerMunicipality" className="rounded-l-none w-full">
              <SelectValue placeholder={customerRegion ? "البلدية" : "الرجاء اختيار الولاية أولا"} />
            </SelectTrigger>
            <SelectContent>
              {filteredCommunes.map((commune) => (
                <SelectItem key={commune.code} value={commune.code}>
                  {commune.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Adresse */}
      <div className="space-y-2">
        <Label htmlFor="customerAddress">Adresse</Label>
        <div className="flex">
          <div className="bg-gray-200 flex items-center justify-center p-2 rounded-l-md">
            <Home className="h-4 w-4 text-gray-500" />
          </div>
          <Textarea
            id="customerAddress"
            placeholder="العنوان"
            value={customerAddress}
            onChange={(e) => setCustomerAddress(e.target.value)}
            rows={3}
            className="rounded-l-none min-h-[80px]"
          />
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="note">Notes</Label>
        <div className="flex">
          <div className="bg-gray-200 flex items-center justify-center p-2 rounded-l-md">
            <MapPin className="h-4 w-4 text-gray-500" />
          </div>
          <Textarea
            id="note"
            placeholder="ملاحظات"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="rounded-l-none min-h-[80px]"
          />
        </div>
      </div>
    </div>
  );
};

export default SimpleOrderForm;
